/**
 * CU-14zefhy0dgu MAP-HM: HERE maps in HaulierMagic Assistant screenshots (same approach as RouteMagic MAP-RM).
 *
 * HERE draws its tiles with WebGL and does not keep the drawing buffer, so a DOM screenshot reads every map canvas back
 * blank. Turning on `preserveDrawingBuffer` (or patching getContext globally) costs GPU time on every frame for every
 * user, so instead each HERE map registers itself here (the shared `HereMapProvider` does it for every HM map screen),
 * and only at capture time, only for maps on screen:
 * - `map.capture()` (HERE API: renders one frame and reads it in the same task) gives a 2D canvas of the map;
 * - that image is put in a temporary `<img data-ai-map-snapshot>` right after the map's own `<canvas>`, at the same
 *   position and size, so DOM markers, bubbles and map controls (later siblings) still sit on top of it;
 * - the screenshot clones the page with those images, then they are removed.
 * A map that fails or is slow is skipped (it stays blank as before); a map never makes the screenshot fail.
 */

/** The parts of an H.Map this needs. */
export interface CapturableMap {
    capture: (
        callback: (canvas: HTMLCanvasElement) => void,
        capturables?: unknown[],
        x1?: number,
        y1?: number,
        x2?: number,
        y2?: number,
        errback?: (error: unknown) => void
    ) => void;
}

interface RegisteredMap {
    map: CapturableMap;
    container: HTMLElement;
}

/** Per-map limit for one HERE capture (the whole screenshot keeps its own 15 s limit). */
export const MAP_SNAPSHOT_TIMEOUT_MS = 4000;
export const MAP_SNAPSHOT_ATTR = 'data-ai-map-snapshot';

const registered = new Set<RegisteredMap>();

/**
 * Called right after `new H.Map(...)` (HM: `HereMapProvider.initialize`). Returns an idempotent unregister for the
 * map's teardown (call it before `map.dispose()`).
 */
export function registerScreenshotMap(map: CapturableMap | null | undefined, container: HTMLElement | null | undefined): () => void {
    if (!map || typeof map.capture !== 'function' || !container) {
        return () => undefined;
    }
    const entry: RegisteredMap = {map, container};
    registered.add(entry);
    return () => {
        registered.delete(entry);
    };
}

/** Number of maps currently registered (tests / diagnostics). */
export function registeredMapCount(): number {
    return registered.size;
}

/** True when the box has a size and at least part of it is inside the viewport. */
export function isOnScreen(
    rect: {top: number; left: number; width: number; height: number} | null | undefined,
    viewportWidth: number,
    viewportHeight: number
): boolean {
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) {
        return false;
    }
    return rect.top < viewportHeight && rect.left < viewportWidth && rect.top + rect.height > 0 && rect.left + rect.width > 0;
}

/** HERE's capture as a promise: the map's canvas, or null on error / timeout. */
export function captureMap(
    map: CapturableMap,
    width: number,
    height: number,
    timeoutMs = MAP_SNAPSHOT_TIMEOUT_MS
): Promise<HTMLCanvasElement | null> {
    return new Promise((resolve) => {
        let done = false;
        const finish = (canvas: HTMLCanvasElement | null) => {
            if (!done) {
                done = true;
                clearTimeout(timer);
                resolve(canvas);
            }
        };
        const timer = setTimeout(() => finish(null), timeoutMs);
        try {
            map.capture(
                (canvas) => finish(canvas || null),
                [],
                0,
                0,
                Math.round(width),
                Math.round(height),
                () => finish(null)
            );
        } catch (e) {
            finish(null);
        }
    });
}

function toDataUrl(canvas: HTMLCanvasElement): string | null {
    try {
        const url = canvas.toDataURL('image/png');
        return url && url.startsWith('data:image/') && url.length > 'data:image/png;base64,'.length ? url : null;
    } catch (e) {
        return null; // tainted canvas
    }
}

/** Puts a captured map image right after the map's canvas, at the canvas's own position and size. */
export function insertSnapshot(mapCanvas: HTMLCanvasElement, dataUrl: string): HTMLImageElement | null {
    const parent = mapCanvas.parentNode;
    if (!parent) {
        return null;
    }
    const img = mapCanvas.ownerDocument.createElement('img');
    img.setAttribute(MAP_SNAPSHOT_ATTR, '');
    img.setAttribute('alt', '');
    const style = img.style;
    style.setProperty('position', 'absolute');
    style.setProperty('left', `${mapCanvas.offsetLeft}px`);
    style.setProperty('top', `${mapCanvas.offsetTop}px`);
    style.setProperty('width', `${mapCanvas.clientWidth}px`);
    style.setProperty('height', `${mapCanvas.clientHeight}px`);
    style.setProperty('pointer-events', 'none');
    style.setProperty('margin', '0');
    style.setProperty('max-width', 'none');
    img.src = dataUrl;
    parent.insertBefore(img, mapCanvas.nextSibling);
    return img;
}

/** One capture's map overlays: `ready` resolves once every on-screen map has its image (or was skipped); never rejects. */
export interface MapSnapshots {
    ready: Promise<void>;
    /** Removes every overlay, also ones still on their way (a capture that timed out must not leave a still map behind). */
    remove: () => void;
}

async function snapshotOne(
    entry: RegisteredMap,
    viewportWidth: number,
    viewportHeight: number,
    timeoutMs: number,
    cancelled: () => boolean
): Promise<HTMLImageElement | null> {
    const {map, container} = entry;
    if (!container.isConnected || !isOnScreen(container.getBoundingClientRect(), viewportWidth, viewportHeight)) {
        return null;
    }
    const mapCanvas = container.querySelector('canvas');
    if (!mapCanvas || !(mapCanvas.clientWidth > 0) || !(mapCanvas.clientHeight > 0)) {
        return null;
    }
    const shot = await captureMap(map, mapCanvas.clientWidth, mapCanvas.clientHeight, timeoutMs);
    const url = shot ? toDataUrl(shot) : null;
    if (!url || !mapCanvas.isConnected || cancelled()) {
        return null;
    }
    const img = insertSnapshot(mapCanvas, url);
    if (img && typeof img.decode === 'function') {
        await img.decode().catch(() => undefined);
    }
    return img;
}

/**
 * Starts overlaying a still image on every registered HERE map that is on screen. `remove()` is idempotent and also
 * cancels overlays that have not landed yet.
 */
export function overlayMapSnapshots(viewportWidth: number, viewportHeight: number, timeoutMs = MAP_SNAPSHOT_TIMEOUT_MS): MapSnapshots {
    const images: HTMLImageElement[] = [];
    let removed = false;
    const cancelled = () => removed;
    const ready = Promise.all(
        Array.from(registered).map((entry) =>
            snapshotOne(entry, viewportWidth, viewportHeight, timeoutMs, cancelled)
                .then((img) => {
                    if (!img) {
                        return;
                    }
                    if (removed) {
                        img.remove(); // landed after remove() (e.g. while decoding)
                    } else {
                        images.push(img);
                    }
                })
                .catch(() => undefined)
        )
    ).then(() => undefined);
    return {
        ready,
        remove: () => {
            if (removed) {
                return;
            }
            removed = true;
            images.forEach((img) => img.remove());
            images.length = 0;
        }
    };
}
